/**
 * /de/audio-transkribieren — Transkription reframe bridge (DE, single-locale).
 *
 * Targets audio transkribieren (2.7k) / transkribieren kostenlos (1.8k) /
 * audiodatei transkribieren. Reframe: Transkript ist Rohmaterial, kein
 * Ergebnis. No EN sibling.
 */
import { MarketingShell } from './MarketingShell';

export function DeAudioTranskribieren() {
  return (
    <MarketingShell
      currentLocale="de"
      hreflangPath="/audio-transkribieren"
      hreflangLocales={['de']}
      documentTitle="Audio transkribieren (kostenlos) – und was danach zählt · Draftlize"
      documentDescription="Audio schnell und kostenlos in Text umwandeln — plus der Schritt, den alle vergessen: aus dem Transkript die Entscheidungen herausziehen, die wirklich zählen."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guide · Transkription</span>
          <h1 className="serif" style={{ fontSize: 'clamp(36px, 4.8vw, 58px)', lineHeight: 1.05, margin: '12px 0 24px' }}>
            Audio transkribieren –<br />
            <em>das Transkript ist erst der Anfang.</em>
          </h1>
          <p className="lede">
            Aufnahme rein, sauberes Transkript raus – in Sekunden. Tools wie Notta, PLAUD oder Otter sind richtig gut darin, festzuhalten, was gesagt wurde. Nutze sie ruhig weiter. Aber ein Transkript ist <em>Rohmaterial</em>, kein Ergebnis. Das, worauf es im Meeting ankam – die <em>Entscheidung</em>, die ihr getroffen habt, und warum – verschwindet in 4.000 Wörtern, landet in einem Dokument, das niemand wieder öffnet, und ist in Wochen veraltet. Hier geht es um den Schritt nach dem Transkribieren: aus den Entscheidungen im Transkript lebendigen Produktkontext machen.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Audio kostenlos transkribieren – in drei Schritten.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ol style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 22 }}>
            <li><strong>Datei wählen</strong> – MP3, M4A, WAV oder eine Sprachnachricht.</li>
            <li><strong>Transkribieren lassen</strong> – ein KI-Transkriptionstool wandelt Sprache in Text um, meist mit Gratis-Kontingent.</li>
            <li><strong>Korrigieren</strong> – Namen und Fachbegriffe gegenlesen, dann exportieren.</li>
          </ol>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--ink-1)', margin: '22px 0 0' }}>
            Das funktioniert für Audiodateien, Sprachnachrichten und MP3s gleichermaßen. Nur: Je genauer das Transkript, desto größer der Textberg – und desto tiefer liegt die eine Entscheidung darin begraben. Mitschreiben ist nicht mehr der Engpass. <em>Herausziehen</em> ist es.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">Rohmaterial vs. lebendiger Kontext</span>
            <h2 className="serif">Wo das Transkript endet, <em>beginnt die Arbeit.</em></h2>
          </div>
          <p>Dein Transkriptionstool und Draftlize machen verschiedene Jobs. Das eine hält das Gespräch fest; das andere hält die Entscheidungen darin aktuell. Behalte beide.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th>Transkriptionstool</th>
              <th className="ours">Draftlize</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Job</td><td>Festhalten, was gesagt wurde</td><td className="ours yes">Aktuell halten, was entschieden wurde</td></tr>
            <tr><td>Ergebnis</td><td className="partial">Transkript + Zusammenfassung</td><td className="ours yes">Entscheidungskarten im Graphen</td></tr>
            <tr><td>Eine Entscheidung kippt eine frühere</td><td className="no">Zwei lose Notizen</td><td className="ours yes">Verknüpft; die alte wird „stale“</td></tr>
            <tr><td>Drei Wochen später</td><td className="no">Im Dokument vergraben</td><td className="ours yes">Adressierbar, aktuell oder markiert</td></tr>
            <tr><td>Ein KI-Agent kann es nutzen</td><td className="partial">Liest flachen Text</td><td className="ours yes">Liest + schreibt via MCP</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Mach aus den Entscheidungen <em>lebendigen Kontext.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            Lass das Transkribieren beim Tool. Entscheidend ist, was mit den wenigen Entscheidungen im Text passiert – in Draftlize hören sie auf, Zeilen in einer Zusammenfassung zu sein, und werden ein Graph, der wahr bleibt.
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Entscheidungen werden Karten, keine Zeilen</h3>
            <p>Füge eine Zusammenfassung in Draftlize ein, und die Entscheidungen darin werden typisierte Karten – Entscheidung, Begründung, Alternativen, Owner. Die Stunde Gespräch schrumpft auf das Wenige, das sie überleben muss, je per ID adressierbar.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Neue Entscheidung markiert die alte</h3>
            <p>Das Meeting diese Woche kippt eine Entscheidung von letztem Monat. Weil sie verknüpft sind, setzt die neue die alte – und jede Spec darauf – automatisch auf „stale“. Das nächste Meeting verhandelt Geklärtes nicht neu.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Dein KI-Agent liest das Entschiedene</h3>
            <p>Die Entscheidungen werden Kontext, den Claude Code oder Cursor über MCP lesen – der Agent für deine nächste Spec weiß schon, was das Team am Dienstag entschieden hat und warum.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Dein Tool transkribiert das Meeting perfekt. Es kann dir nur nicht sagen, welche der Entscheidungen darin drei Wochen später noch gelten.
        </blockquote>
        <cite>Behalte das Transkriptionstool. Draftlize hält die Entscheidungen daraus lebendig.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Häufige Fragen.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'Welches kostenlose Transkriptionstool ist gut?', a: 'Notta, PLAUD oder Otter – nimm, was zu deinem Workflow passt. Draftlize transkribiert nicht. Es setzt an, wenn das Transkript existiert, und macht aus den Entscheidungen darin strukturierte, aktuelle Karten.' },
            { q: 'Wie genau ist die Transkription?', a: 'Moderne KI-Transkription ist auch auf Deutsch praxistauglich. Aber je genauer, desto größer der Textberg – und desto wichtiger der Schritt, die Entscheidungen herauszuziehen.' },
            { q: 'Kann ich lange Aufnahmen transkribieren?', a: 'Die meisten Tools können das. Das Problem ist nicht die Länge, sondern das Danach: Der Wert einer Stunde Meeting steckt in wenigen Entscheidungen. Draftlize bewahrt genau die.' },
            { q: 'Was mache ich mit dem Transkript?', a: 'Füge die Zusammenfassung in Draftlize ein und halte die Entscheidungen als Karten fest – Entscheidung, Begründung, Owner. Nicht das ganze Transkript, sondern das Wenige, das das Meeting überleben muss.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Aus dem Transkript werden Entscheidungen <em>– gratis mit $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>$5 Gratis-Guthaben für neue Accounts</span>
          <span>Zahle nur, was du nutzt</span>
          <span>Guthaben verfällt nie</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Behalte dein Transkriptionstool. Füge die Zusammenfassung in Draftlize ein – die Entscheidungen darin werden lebendige Karten: verknüpft, aktuell und für deinen KI-Agenten lesbar.
          </p>
          <a className="btn-primary" href="/">Gratis mit $5 starten</a>
        </div>
      </section>
    </MarketingShell>
  );
}
